import type { TFunction } from 'i18next';

import type { ChartData } from '../model/shared-types';

/**
 * The API sends chart series names as English text, and for the fixed stock,
 * movement and service-user series that text is the backend's own wording —
 * "Sane", "Committed", "Picked Up" — where legacy RITA says "Good Condition",
 * "Committed Stock", "Collected from Customer". Map those onto the legacy keys so
 * the legend reads the same and follows the locale.
 *
 * Only this closed set is mapped. Everything else a series can be named —
 * a transport mode, an origin, an organisation — passes through untouched.
 */
const SERIES_LABEL_KEYS: Record<string, string> = {
  Sane: 'dispatch.sane.cond.label',
  Damaged: 'dispatch.damaged.cond.label',
  Committed: 'releaseOrder.committed.stock.desc',
  'Damaged Committed': 'releaseOrder.committed.stock.damage.desc',
  'Reported Lost': 'disp.col.lost.desc',
  Received: 'welcome.received.label',
  Dispatched: 'welcome.dispatched.label',
  'Released to Customer': 'welcome.released.label',
  'Lost from Stock': 'dispatch.loss.history.title',
  'Released at Warehouse': 'welcome.serviceuser.transfered.desc',
  'Released at Customer': 'welcome.serviceuser.released.desc',
  'Customer Delivery': 'receive.customer.label',
  'Picked Up': 'welcome.serviceuser.collect.desc'
};

/**
 * Transport broken down by condition names its series `<condition> - <vehicle
 * category>` ("Sane - Road") and sends the category again as `stack`. Only the
 * condition part is translated, onto the legacy labels.
 */
const CONDITION_LABEL_KEYS: Record<string, string> = {
  Sane: 'dispatch.sane.cond.label',
  Damaged: 'dispatch.damaged.cond.label',
  Lost: 'receive.lostintransit.label'
};

function translateName({ name, stack }: ChartData['series'][number], t: TFunction): string | undefined {
  const key = SERIES_LABEL_KEYS[name];
  if (key) return t(key);

  const suffix = ` - ${stack}`;
  if (!stack || !name.endsWith(suffix)) return undefined;
  const conditionKey = CONDITION_LABEL_KEYS[name.slice(0, -suffix.length)];
  return conditionKey && `${t(conditionKey)}${suffix}`;
}

export function translateSeriesNames(data: ChartData, t: TFunction): ChartData {
  const names = data.series.map((series) => translateName(series, t));
  if (!names.some(Boolean)) return data;

  return {
    ...data,
    series: data.series.map((series, index) => {
      const name = names[index];
      return name ? { ...series, name } : series;
    })
  };
}
